<template>
  <div class="trade-group-page">
    <trade-group-products
      v-if="showProductPanel"
      :group="currentProductGroup"
      @back="closeProductPanel"
    />
    <template v-else>
    <div class="info-bar">
      新增交易组别保存、「添加国家」按默认模板复制配置时，都会同步通过 MT5 WebAPI 建组（「添加国家」有几个模板就等几次，请勿重复提交）；建组失败不影响配置落库、可在列表中对失败的行点「重试」；已创建成功的组别会锁定国家、场景、组别ID和 MT5 组路径。
    </div>

    <div class="trade-group-layout">
      <div class="group-sidebar">
        <div class="sidebar-add-area">
          <el-button
            v-auth="'business:tradeGroup:save'"
            v-if="!showCountrySelect"
            type="primary"
            size="small"
            icon="el-icon-plus"
            style="width:100%;"
            @click="openCountrySelect"
          >添加国家配置</el-button>
          <div v-else class="select-with-close">
            <el-select
              ref="countrySelect"
              v-model="addingCountry"
              filterable
              placeholder="搜索国家..."
              popper-class="country-select-popper"
              size="small"
              style="flex:1;"
              @change="onCountrySelect"
            >
              <el-option
                v-for="item in availableCountryOptions"
                :key="item.countryCode"
                :label="countryOptionLabel(item)"
                :value="item.countryCode"
              />
            </el-select>
            <i class="el-icon-close close-select-btn" @click="onCountryClear"></i>
          </div>
        </div>

        <div class="country-list">
          <div
            class="country-item"
            :class="{ active: currentCountry === 'default' }"
            @click="switchCountry('default')"
          >
            <span class="country-icon">⚙️</span>
            <span class="country-name">默认配置</span>
          </div>
          <div
            v-for="item in sidebarCountries"
            :key="item.countryCode"
            class="country-item"
            :class="{ active: currentCountry === item.countryCode }"
            @click="switchCountry(item.countryCode)"
          >
            <span class="country-icon">📍</span>
            <span class="country-name">{{ countryOptionLabel(item) }}</span>
            <i
              v-auth="'business:tradeGroup:delete'"
              class="el-icon-delete country-delete"
              title="删除该国家的全部交易组别配置"
              @click.stop="handleCountryMenu('remove', item)"
            ></i>
          </div>
        </div>
      </div>

      <div class="group-content" v-loading="loading">
        <div class="content-header">
          <div>
            <div class="content-title-row">
              <div class="content-title">{{ currentCountryTitle }}</div>
              <el-tag size="mini" effect="plain" class="country-type-tag">{{ currentCountryTag }}</el-tag>
            </div>
            <div class="content-subtitle">
              {{ currentCountrySubtitle }}
            </div>
          </div>
          <div class="header-actions">
            <el-button
              v-auth="'business:tradeGroup:sync'"
              size="small"
              icon="el-icon-refresh"
              :loading="syncingProductCount"
              @click="syncProductCount"
            >
              更新MT5组产品数量
            </el-button>
            <el-button v-auth="'business:tradeGroup:log'" size="small" icon="el-icon-document" @click="openAllLogs">业务日志</el-button>
          </div>
        </div>

        <div class="scene-tabs">
          <div
            class="scene-tab"
            :class="{ active: activeScene === 1 }"
            @click="switchScene(1)"
          >
            <span class="scene-icon scene-icon-real"></span>
            <span>真实交易组</span>
            <span class="scene-count">{{ sceneCount(1) }}</span>
          </div>
          <div
            class="scene-tab"
            :class="{ active: activeScene === 2 }"
            @click="switchScene(2)"
          >
            <span class="scene-icon scene-icon-demo"></span>
            <span>模拟交易组</span>
            <span class="scene-count">{{ sceneCount(2) }}</span>
          </div>
          <div
            class="scene-tab"
            :class="{ active: activeScene === 3 }"
            @click="switchScene(3)"
          >
            <span class="scene-icon scene-icon-guest"></span>
            <span>游客组</span>
            <span class="scene-count">{{ sceneCount(3) }}</span>
          </div>
        </div>

        <div class="group-panel">
          <div class="panel-header">
            <div class="panel-title">{{ scenePanelTitle }}</div>
            <div class="panel-actions">
              <el-button v-auth="'business:tradeGroup:save'" type="primary" size="small" icon="el-icon-plus" @click="openAdd">
                新增{{ activeSceneLabel }}
              </el-button>
            </div>
          </div>

          <div v-if="sceneGroups.length" class="group-table-wrap">
            <el-table
              :data="sceneGroups"
              border
              size="small"
              style="width: 100%;"
              class="group-table"
            >
            <el-table-column label="#" width="48" align="center">
              <template slot-scope="scope">
                {{ scope.$index + 1 }}
              </template>
            </el-table-column>
            <el-table-column prop="groupCode" label="组别ID" min-width="126" show-overflow-tooltip />
            <el-table-column label="默认币种" width="88" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" class="currency-tag">{{ scope.row.currency || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="组别杠杆" width="88" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.groupLeverage">{{ '1:' + scope.row.groupLeverage }}</span>
                <span v-else class="muted-text">-</span>
              </template>
            </el-table-column>
            <!-- 最低门槛是金额（单位取本行默认币种）：0 与空值都表示「不设门槛」，统一显示 - -->
            <el-table-column label="入金门槛" width="106" align="right">
              <template slot-scope="scope">
                <span v-if="Number(scope.row.minThreshold) > 0">{{ formatAmount(scope.row.minThreshold) }}</span>
                <span v-else class="muted-text">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="groupName" label="交易组别" min-width="120" show-overflow-tooltip />
            <el-table-column label="MT5交易组别" min-width="180" show-overflow-tooltip>
              <template slot-scope="scope">
                <span
                  v-auth="'business:tradeGroup:product'"
                  v-if="scope.row.mt5GroupPath"
                  class="mt5-path-link"
                  title="点击查看交易品种配置"
                  @click="openProducts(scope.row)"
                >{{ scope.row.mt5GroupPath }}</span>
                <span v-else class="muted-text">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="description" label="描述" min-width="160" show-overflow-tooltip />
            <el-table-column label="产品数量" width="92" align="center">
              <template slot-scope="scope">
                {{ scope.row.productCount == null ? 0 : scope.row.productCount }}
              </template>
            </el-table-column>

            <el-table-column label="设为默认" width="88" align="center">
              <template slot-scope="scope">
                <el-radio
                  v-auth="'business:tradeGroup:setDefault'"
                  v-model="defaultGroupId"
                  :label="scope.row.id"
                  class="default-radio"
                  title="设为默认组"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="250" align="center" class-name="group-action-column">
              <template slot-scope="scope">
                <el-button
                  v-auth="'business:tradeGroup:create'"
                  v-if="scope.row.createStatus !== 2"
                  type="text"
                  size="mini"
                  class="action-link action-link-primary"
                  @click="handleCreate(scope.row, false)"
                  :disabled="scope.row.createStatus === 1"
                >
                  创建
                </el-button>
                <el-button
                  v-auth="'business:tradeGroup:create'"
                  v-if="scope.row.createStatus === 3"
                  type="text"
                  size="mini"
                  class="action-link action-link-warning"
                  @click="handleCreate(scope.row, true)"
                >
                  重试
                </el-button>
                <el-button
                  v-auth="'business:tradeGroup:product'"
                  v-if="scope.row.createStatus === 2"
                  type="text"
                  size="mini"
                  class="action-link action-link-primary"
                  @click="openProducts(scope.row)"
                >
                  产品
                </el-button>
                <el-button v-auth="'business:tradeGroup:edit'" type="text" size="mini" class="action-link action-link-primary" @click="openEdit(scope.row)">编辑</el-button>
                <el-button v-auth="'business:tradeGroup:log'" type="text" size="mini" class="action-link action-link-info" @click="openLog(scope.row)">日志</el-button>
                <el-button v-auth="'business:tradeGroup:delete'" type="text" size="mini" class="action-link action-link-danger" @click="deleteRow(scope.row)">删除</el-button>
              </template>
            </el-table-column>
            </el-table>
          </div>

          <div v-else class="empty-panel">
            <div class="empty-title">当前场景暂无交易组别配置</div>
            <div class="empty-desc">可先新增当前国家下的{{ activeSceneLabel }}。</div>
            <el-button v-auth="'business:tradeGroup:save'" type="primary" size="small" icon="el-icon-plus" @click="openAdd">
              新增{{ activeSceneLabel }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="760px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="110px" size="small">
        <!-- 国家/国家名称不在这里选：弹窗本来就是从左侧选中的国家（或「默认配置」）进来的，
             两个字段的值由 fillCountryFromSidebar 按当前国家带出，仍然随表单提交 -->
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="组别场景" required>
              <el-radio-group v-model="form.groupScene" :disabled="lockImmutableFields()">
                <el-radio-button :label="1">真实组</el-radio-button>
                <el-radio-button :label="2">模拟组</el-radio-button>
                <el-radio-button :label="3">游客组</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序">
              <el-input-number v-model="form.sort" :min="0" style="width: 100%;" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="组别ID" required>
              <el-input
                v-model="form.groupCode"
                disabled
                :placeholder="form.id ? '系统生成的组别ID' : '保存时自动按国家递增生成，如 TG-CN-01'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="组别名称" required>
              <el-input v-model="form.groupName" placeholder="如 标准真实组" />
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 这一行给 MT5组路径 16 栅格：路径（如 real\DirectCustomer\Actual\DEFAULT\Standard）
             比半栏宽，12 栅格下示例与已填值的尾部都会被输入框裁掉 -->
        <el-row :gutter="16">
          <el-col :span="16">
            <el-form-item label="MT5组路径" required>
              <el-input v-model="form.mt5GroupPath" :disabled="lockImmutableFields()" :placeholder="mt5GroupPathPlaceholder" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="默认币种" required>
              <el-select v-model="form.currency" placeholder="请选择默认币种" style="width: 100%;">
                <el-option label="USD" value="USD" />
                <el-option label="USC" value="USC" />
                <el-option label="EUR" value="EUR" />
                <el-option label="GBP" value="GBP" />
                <el-option label="JPY" value="JPY" />
                <el-option label="CHF" value="CHF" />
                <el-option label="RUR" value="RUR" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 等级门槛暂时隐藏；字段仍保留在 form 里随表单提交，恢复显示只需把这行 form-item 加回来 -->
        <el-row :gutter="16">
          <el-col :span="12">
            <!-- 组别杠杆：编辑时也可改（原「已建组即锁定」的限制已放开）。
                 已建组成功的记录，后台保存时会先只把 DemoLeverage 同步到 MT5，成功后才落库；
                 值没变则完全不调 MT5 —— 所以这里不需要（也不该）再按创建状态禁用 -->
            <el-form-item label="组别杠杆" required>
              <el-select
                v-model="form.groupLeverage"
                placeholder="请选择组别杠杆"
                style="width: 100%;"
              >
                <el-option v-for="item in leverageOptions" :key="item" :label="'1:' + item" :value="item" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <!-- 原「创建状态」只读回显已隐藏（需求：这行隐藏，位置让给「最低门槛」）。
                 创建进度在列表行的创建/重试/同步按钮上已经能看出来，弹窗里再放一遍是重复信息。
                 form.createStatus 仍保留提交：lockImmutableFields() 靠它锁住国家/场景/组别ID/MT5组路径
                 （组别杠杆已不再受它限制）。
                 要恢复这行回显，把原来的 form-item 加回来即可（createStatusText 仍在）。 -->
            <!-- 门槛金额的计价币种就是本组的默认币种（后端按 t_trade_group.currency 口径比对，
                 不再假设「一律按美元填」）：标签里带上币种，避免给人填 100 却不知是美元还是美分 -->
            <el-form-item :label="'入金门槛（' + (form.currency || 'USD') + '）'">
              <el-input-number
                v-model="form.minThreshold"
                :min="0"
                :precision="2"
                placeholder="0 表示不设门槛"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="默认组">
              <el-switch v-model="form.isDefault" :active-value="1" :inactive-value="2" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="启用状态">
              <el-switch v-model="form.status" :active-value="1" :inactive-value="2" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="2" placeholder="组别业务说明" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="补充备注" />
        </el-form-item>
        <el-form-item label="MT5返回消息" v-if="form.id">
          <el-input v-model="form.mt5Retmsg" type="textarea" :rows="2" disabled />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="save">确认保存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="logDialogTitle"
      :visible.sync="logDialogVisible"
      width="920px"
      :close-on-click-modal="false"
    >
      <div class="log-filter-bar">
        <span class="log-group-title">
          {{ currentLogGroup.id ? `${currentLogGroup.groupCode || '-'} / ${currentLogGroup.groupName || '-'}` : '全部交易组别日志' }}
        </span>
        <el-input
          v-if="!currentLogGroup.id"
          v-model="logFilter.groupCode"
          placeholder="组别ID"
          clearable
          size="small"
          style="width: 140px;"
        />
        <el-select v-model="logFilter.actionType" placeholder="全部操作类型" clearable size="small" style="width: 140px;">
          <el-option label="新增配置" :value="1" />
          <el-option label="编辑配置" :value="2" />
          <el-option label="设为默认" :value="3" />
          <el-option label="取消默认" :value="4" />
          <el-option label="启用组别" :value="5" />
          <el-option label="禁用组别" :value="6" />
          <el-option label="发起创建" :value="7" />
          <el-option label="创建成功" :value="8" />
          <el-option label="创建失败" :value="9" />
          <el-option label="删除配置" :value="10" />
          <el-option label="更新产品数量" :value="11" />
          <el-option label="新增关联产品" :value="12" />
          <el-option label="修改关联产品" :value="13" />
          <el-option label="移除关联产品" :value="14" />
          <el-option label="同步组别杠杆" :value="15" />
        </el-select>
        <el-input v-model="logFilter.operator" placeholder="操作人" clearable size="small" style="width: 120px;" />
        <el-date-picker v-model="logFilter.startDate" type="date" placeholder="开始日期" value-format="yyyy-MM-dd" size="small" style="width: 140px;" />
        <el-date-picker v-model="logFilter.endDate" type="date" placeholder="结束日期" value-format="yyyy-MM-dd" size="small" style="width: 140px;" />
        <el-button size="small" @click="resetLogFilter">重置</el-button>
        <el-button type="primary" size="small" @click="fetchLogs">搜索</el-button>
      </div>

      <el-table :data="logTableData" border v-loading="logLoading" size="small" style="width: 100%;">
        <el-table-column prop="operateTime" label="操作时间" width="170" />
        <el-table-column v-if="!currentLogGroup.id" prop="groupCode" label="组别ID" width="120" />
        <el-table-column v-if="!currentLogGroup.id" prop="groupName" label="组别名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="操作类型" width="120" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="actionTypeTag(scope.row.actionType)">
              {{ scope.row.actionTypeName || actionTypeText(scope.row.actionType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="operator" label="操作人" width="120" />
        <el-table-column prop="actionDetail" label="变更内容" min-width="360" show-overflow-tooltip />
      </el-table>

      <el-pagination
        @size-change="(v) => { logPageSize = v; fetchLogs() }"
        @current-change="(v) => { logPageNum = v; fetchLogs() }"
        :current-page="logPageNum"
        :page-sizes="[10, 20, 50]"
        :page-size="logPageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="logTotal"
      />
    </el-dialog>
    </template>
  </div>
</template>

<script>
import {
  tradeGroupAddOrUpdate,
  tradeGroupAddCountryConfig,
  tradeGroupCreateOne,
  tradeGroupDeleteBatchIds,
  tradeGroupDetail,
  tradeGroupList,
  tradeGroupLogList,
  tradeGroupRetryCreate,
  tradeGroupSyncProductCount,
  tradeGroupSetDefault,
  tradeGroupStatus
} from '@/api/tradeGroup'
import { countryList } from '@/api/regConfig'
import TradeGroupProducts from './components/groupProducts'

// 组别场景标签，与后端 TradeGroupSceneEnum 保持一致（1真实组 2模拟组 3游客组）
const SCENE_LABELS = {
  1: '真实交易组',
  2: '模拟交易组',
  3: '游客组'
}

// MT5 组路径输入框的示例（随组别场景切换，三种场景的路径格式不同）。
// {country} 运行时替换成当前所选国家的 countryCode —— 选「默认配置」时这里就是字面量 DEFAULT，
// 与后端「添加国家」把模板里的 DEFAULT 段换成对应国家代码的口径一致（TradeGroupServiceImpl.buildCountryGroupPath）。
const MT5_GROUP_PATH_PLACEHOLDERS = {
  1: 'real\\DirectCustomer\\Actual\\{country}\\Standard',
  2: 'demo\\DirectCustomer\\{country}\\Standard',
  3: 'demo\\DirectCustomer\\GUEST\\{country}\\Standard'
}

export default {
  name: 'TradeGroupList',
  components: {
    TradeGroupProducts
  },
  computed: {
    hasDefaultTemplate() {
      return this.allGroupData.some(item => item.countryCode === 'DEFAULT')
    },
    availableCountryOptions() {
      return this.countryOptions.filter(item => !this.sidebarCountries.some(country => country.countryCode === item.countryCode))
    },
    currentCountryTitle() {
      if (this.currentCountry === 'default') {
        return '默认配置'
      }
      return this.countryDisplay(this.currentCountry)
    },
    currentCountryTag() {
      return this.currentCountry === 'default' ? '默认' : '国家'
    },
    currentCountrySubtitle() {
      if (this.currentCountry === 'default') {
        return '默认配置作为国家交易组模板，新增国家时将按默认配置自动复制生成对应国家组别。'
      }
      return `${this.currentCountryTitle} 的交易组别配置，按真实交易组和模拟交易组分别维护。`
    },
    countryGroups() {
      const list = this.currentCountry === 'default'
        ? this.allGroupData.filter(item => item.countryCode === 'DEFAULT')
        : this.allGroupData.filter(item => item.countryCode === this.currentCountry)
      return this.sortGroupList(list)
    },
    sceneGroups() {
      return this.countryGroups.filter(item => item.groupScene === this.activeScene)
    },
    // 「设为默认」列的单选：选中态由当前默认组推导，点击只是设置动作的入口。
    // 用 get/set 计算属性而不是独立的 data 字段，取消确认弹窗或接口失败时选中态会自动
    // 回弹到真实的默认组，不需要手工回滚。
    defaultGroupId: {
      get() {
        const current = this.sceneGroups.find(item => item.isDefault === 1)
        return current ? current.id : null
      },
      set(id) {
        const row = this.sceneGroups.find(item => item.id === id)
        if (row && row.isDefault !== 1) {
          this.handleSetDefault(row)
        }
      }
    },
    activeSceneLabel() {
      return SCENE_LABELS[this.activeScene] || ''
    },
    scenePanelTitle() {
      return `${this.activeSceneLabel}列表（共 ${this.sceneGroups.length} 个）`
    },
    // 组别杠杆下拉：界面显示 1:500…1:2，提交给后台的是冒号后的数字（500…2）
    leverageOptions() {
      const list = [500, 400, 300, 200, 175, 150, 125, 100, 75, 66, 50, 33, 25, 20, 15, 10, 5, 3, 2]
      const raw = this.form.groupLeverage
      if (raw === null || raw === undefined || raw === '') {
        return list
      }
      const current = Number(raw)
      if (!Number.isFinite(current) || list.includes(current)) {
        return list
      }
      // 历史数据里可能存着不在档位内的值（MT5 回写缺失时为 0），补一项进去，
      // 否则编辑态下拉会显示一个裸数字，看着像坏了
      return list.concat(current).sort((a, b) => b - a)
    },
    // MT5 组路径示例：跟着「组别场景」走，真实组/模拟组/游客组三种格式不同；
    // 中间的国家段跟着左侧选中的国家走（form.countryCode 由 fillCountryFromSidebar 填，
    // 「默认配置」即字面量 DEFAULT），所以这里取不到值时才退化成 DEFAULT
    mt5GroupPathPlaceholder() {
      const template = MT5_GROUP_PATH_PLACEHOLDERS[this.form.groupScene]
      if (!template) {
        return ''
      }
      return template.replace('{country}', this.form.countryCode || 'DEFAULT')
    }
  },
  data() {
    return {
      loading: false,
      syncingProductCount: false,
      allGroupData: [],
      currentCountry: 'default',
      activeScene: 1,
      showProductPanel: false,
      currentProductGroup: null,
      sidebarCountries: [],
      addingCountry: null,
      showCountrySelect: false,
      dialogVisible: false,
      dialogTitle: '新增交易组别',
      form: this.createEmptyForm(),
      // 打开编辑弹窗时记下的组别杠杆，用于保存成功后判断「本次是否改过杠杆」，
      // 改过才提示「MT5 杠杆已同步」（后台只在杠杆变化时才真正调 MT5）
      originalGroupLeverage: null,
      countryOptions: [],
      logDialogVisible: false,
      logDialogTitle: '交易组别业务日志',
      logLoading: false,
      logTableData: [],
      logTotal: 0,
      logPageNum: 1,
      logPageSize: 10,
      currentLogGroup: {
        id: null,
        groupCode: '',
        groupName: ''
      },
      logFilter: this.createEmptyLogFilter()
    }
  },
  mounted() {
    this.fetchCountries()
    this.fetchGroupOverview()
  },
  methods: {
    createEmptyForm() {
      return {
        id: null,
        countryCode: '',
        countryName: '',
        groupScene: 1,
        groupCode: '',
        groupName: '',
        mt5GroupPath: '',
        currency: 'USD',
        groupLeverage: null,
        minThreshold: null,
        tierCode: '',
        description: '',
        remark: '',
        isDefault: 2,
        sort: 0,
        status: 1,
        createStatus: 0,
        createStatusName: '待创建',
        mt5Retmsg: ''
      }
    },
    createEmptyLogFilter() {
      return {
        groupCode: '',
        actionType: null,
        operator: '',
        startDate: '',
        endDate: ''
      }
    },
    fetchCountries() {
      countryList({ pageNum: 1, pageSize: 500 }).then(res => {
        if (res.code === 0) {
          this.countryOptions = (res.result && res.result.list) || []
          this.rebuildSidebarCountries()
        }
      })
    },
    fetchGroupOverview() {
      this.loading = true
      tradeGroupList({
        pageNum: 1,
        pageSize: 1000
      }).then(res => {
        if (res.code === 0) {
          this.allGroupData = (res.result && res.result.list) || []
          this.rebuildSidebarCountries()
        }
      }).finally(() => {
        this.loading = false
      })
    },
    syncProductCount() {
      this.syncingProductCount = true
      tradeGroupSyncProductCount({
        countryCode: this.currentCountry === 'default' ? 'DEFAULT' : this.currentCountry
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(res.result || '更新成功')
          this.fetchGroupOverview()
          if (this.logDialogVisible) {
            this.fetchLogs()
          }
        }
      }).finally(() => {
        this.syncingProductCount = false
      })
    },
    rebuildSidebarCountries() {
      const configuredMap = new Map()
      this.allGroupData.forEach(item => {
        if (!item.countryCode || configuredMap.has(item.countryCode)) {
          return
        }
        configuredMap.set(item.countryCode, {
          countryCode: item.countryCode,
          countryName: item.countryName
        })
      })
      this.sidebarCountries = Array.from(configuredMap.values())
        .filter(item => item.countryCode !== 'DEFAULT')
        .sort((a, b) => String(a.countryCode).localeCompare(String(b.countryCode)))
      if (this.currentCountry !== 'default' && !this.sidebarCountries.some(item => item.countryCode === this.currentCountry)) {
        this.currentCountry = 'default'
      }
    },
    openCountrySelect() {
      if (!this.hasDefaultTemplate) {
        this.$message.warning('请先在默认配置中新增模板组别，再添加国家配置')
        return
      }
      this.showCountrySelect = true
      this.addingCountry = null
      this.$nextTick(() => {
        this.$refs.countrySelect && this.$refs.countrySelect.focus()
      })
    },
    onCountryClear() {
      this.showCountrySelect = false
      this.addingCountry = null
    },
    onCountrySelect(countryCode) {
      if (!countryCode) {
        return
      }
      const item = this.countryOptions.find(option => option.countryCode === countryCode)
      if (!item) {
        return
      }
      // 全屏蒙层：后端会先复制默认配置、再逐个串行等 MT5 建组（默认模板有几条就等几次往返，
      // 通常 3~9 秒），期间挡住重复点选。成功/失败/异常都要关闭
      const loading = this.$loading({
        lock: true,
        text: '正在复制配置并创建 MT5 交易组，请稍候...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)'
      })
      tradeGroupAddCountryConfig({
        countryCode: item.countryCode,
        countryName: item.countryCname || item.countryName || item.countryCode
      }).then(res => {
        if (res.code !== 0) {
          return
        }
        const list = res.result || []
        // createStatus: 0 待创建 / 1 创建中 / 2 创建成功 / 3 创建失败
        const failed = list.filter(vo => vo.createStatus !== 2)
        if (failed.length === 0) {
          this.$message.success(`国家交易组配置已添加，${list.length} 个 MT5 交易组创建成功`)
        } else {
          // 配置已落库，失败只影响建组：如实报出成功/失败条数与失败组别，指引到列表里逐条「重试」
          this.$message({
            type: 'warning',
            duration: 8000,
            dangerouslyUseHTMLString: true,
            message: `配置已添加：MT5 建组成功 ${list.length - failed.length} 个、失败 ${failed.length} 个<br/>`
              + `失败组别：${failed.map(vo => vo.groupName || vo.mt5GroupPath || vo.groupCode).join('、')}<br/>`
              + '可在列表中分别点「重试」重新创建。'
          })
        }
        this.currentCountry = item.countryCode
        this.showCountrySelect = false
        this.addingCountry = null
        this.fetchGroupOverview()
      }).catch(() => {
        // 非 0 的 code 已由 axios 拦截器弹过错误提示，这里只负责让蒙层关掉
      }).finally(() => {
        loading.close()
      })
    },
    switchCountry(countryCode) {
      this.currentCountry = countryCode
    },
    switchScene(scene) {
      this.activeScene = scene
    },
    sceneCount(scene) {
      return this.countryGroups.filter(item => item.groupScene === scene).length
    },
    sortGroupList(list) {
      return [...(list || [])].sort((a, b) => {
        const sortA = Number(a.sort || 0)
        const sortB = Number(b.sort || 0)
        if (sortA !== sortB) {
          return sortA - sortB
        }
        return String(a.groupCode || '').localeCompare(String(b.groupCode || ''))
      })
    },
    // 国家/国家名称不在弹窗里出现，值一律跟着左侧选中的国家走（「默认配置」就是 DEFAULT）
    fillCountryFromSidebar() {
      if (this.currentCountry === 'default') {
        this.form.countryCode = 'DEFAULT'
        this.form.countryName = '默认配置'
        return
      }
      this.form.countryCode = this.currentCountry
      this.form.countryName = this.countryDisplay(this.currentCountry)
    },
    openAdd() {
      this.dialogTitle = '新增交易组别'
      this.form = this.createEmptyForm()
      this.originalGroupLeverage = null
      this.form.groupScene = this.activeScene
      this.fillCountryFromSidebar()
      this.form.groupCode = ''
      this.dialogVisible = true
    },
    openEdit(row) {
      tradeGroupDetail(row.id).then(res => {
        if (res.code !== 0) return
        this.form = Object.assign(this.createEmptyForm(), res.result || {})
        this.originalGroupLeverage = this.form.groupLeverage
        this.dialogTitle = `编辑交易组别：${this.form.groupCode || ''}`
        this.dialogVisible = true
      })
    },
    handleCountryChange(countryCode) {
      const item = this.countryOptions.find(option => option.countryCode === countryCode)
      if (!item) return
      this.form.countryName = item.countryCname || item.countryName || item.countryCode
      if (!this.form.id) {
        this.form.groupCode = ''
      }
    },
    lockImmutableFields() {
      return !!this.form.id && this.form.createStatus === 2
    },
    // 组别杠杆是数值字段：下拉框选中即为数字、未选时是 null，这里统一兜一层，
    // 保证提交给后台的要么是数字、要么是 null（不会漏出空串）。
    // 编辑态该字段禁用，回传的就是库里原值。
    normalizeGroupLeverage() {
      const raw = this.form.groupLeverage
      if (raw === '' || raw === null || raw === undefined) {
        return null
      }
      return Number(raw)
    },
    // 最低门槛是金额字段：el-input-number 清空后是 null，这里统一收敛成数字再提交 ——
    // 后台对 null 走的是「未传=不改」，界面清空是用户主动取消门槛，必须真的写下去，
    // 所以空值/非法值一律发 0（0 与 null 在展示上都表示「不设门槛」）。
    // :precision="2" 已保证不超过 2 位小数、:min="0" 已挡掉负数，这里只做最后一道兜底。
    normalizeMinThreshold() {
      const raw = this.form.minThreshold
      if (raw === '' || raw === null || raw === undefined) {
        return 0
      }
      const num = Number(raw)
      return Number.isFinite(num) && num > 0 ? num : 0
    },
    save() {
      // 国家/国家名称已从弹窗里隐藏（值取自左侧选中的国家），回显数据缺这两个字段时
      // 按当前国家补上——后台这两个字段都是必填，但不能弹一个用户无处可填的提示
      if (!this.form.countryCode || !this.form.countryName) {
        this.fillCountryFromSidebar()
      }
      if (!this.form.groupName) {
        this.$message.warning('请输入组别名称')
        return
      }
      if (!this.form.mt5GroupPath) {
        this.$message.warning('请输入 MT5 组路径')
        return
      }
      if (!this.form.currency) {
        this.$message.warning('请选择默认币种')
        return
      }
      // 组别杠杆现在编辑时也可改：新增必须选一个（1~500 的整数）；编辑留空表示「不改杠杆」，
      // 因为极少数历史记录的值可能为空，不能反过来卡住该记录的其它字段编辑。
      const leverage = this.normalizeGroupLeverage()
      const isEdit = !!this.form.id
      if (leverage === null) {
        if (!isEdit) {
          this.$message.warning('请选择组别杠杆')
          return
        }
      } else if (!Number.isInteger(leverage) || leverage < 1 || leverage > 500) {
        this.$message.warning('组别杠杆必须是 1~500 的整数')
        return
      }
      const payload = {
        id: this.form.id,
        countryCode: this.form.countryCode,
        countryName: this.form.countryName,
        groupScene: this.form.groupScene,
        groupCode: this.form.groupCode,
        groupName: this.form.groupName,
        mt5GroupPath: this.form.mt5GroupPath,
        currency: this.form.currency,
        // 原样回传界面上的值：后台比对库里值，只有真变了才去同步 MT5（没变不发远程调用）；
        // 界面为空（历史脏值/未选）时回传 null = 不改，免得空值被当成一次修改
        groupLeverage: leverage,
        minThreshold: this.normalizeMinThreshold(),
        tierCode: this.form.tierCode,
        description: this.form.description,
        remark: this.form.remark,
        isDefault: this.form.isDefault,
        sort: this.form.sort,
        status: this.form.status
      }
      // 新增与编辑共用同一个接口，但只有新增会在保存后同步下发 MT5 建组，
      // 提示文案必须分开，否则编辑保存会谎称发生过建组。
      // 编辑时若改过组别杠杆，后台会先同步 MT5 才落库（同步失败整单失败、走不到这里），
      // 所以能走到成功分支就说明 MT5 已同步，提示里可以直接说
      const isAdd = !payload.id
      const leverageSynced = !isAdd && leverage !== null && leverage !== this.originalGroupLeverage
      tradeGroupAddOrUpdate(payload).then(res => {
        // 非 0 是校验类失败（如组别ID/MT5组路径重复）、或 MT5 杠杆同步失败（整单未落库）：
        // 保持弹窗打开、输入不丢，错误提示已由响应拦截器弹出，这里不再重复提示
        if (res.code !== 0) {
          return
        }
        const vo = res.result || {}
        if (!isAdd) {
          this.$message.success(leverageSynced ? '保存成功，MT5组别杠杆已同步' : '保存成功')
        } else if (vo.createStatus === 2) {
          this.$message.success('保存成功，MT5建组成功')
        } else {
          // 配置已落库、只是 MT5 建组失败：不拦保存结果，明确告知并指向列表里的「重试」
          this.$message({
            type: 'warning',
            duration: 8000,
            dangerouslyUseHTMLString: true,
            message: `配置已保存，但 MT5 建组失败：${vo.mt5Retmsg || '未知原因'}<br/>可在列表中找到该组，点「重试」重新创建。`
          })
        }
        this.dialogVisible = false
        this.fetchGroupOverview()
        this.refreshLogsIfNeeded(payload.id)
      })
    },
    toggleStatus(row) {
      const targetStatus = row.status === 1 ? 2 : 1
      tradeGroupStatus(row.id, targetStatus).then(res => {
        if (res.code === 0) {
          this.$message.success('状态更新成功')
          this.fetchGroupOverview()
          this.refreshLogsIfNeeded(row.id)
        }
      })
    },
    handleSetDefault(row) {
      this.$confirm(`确定将组别 ${row.groupCode} 设为默认组吗？`, '提示', { type: 'warning' }).then(() => {
        tradeGroupSetDefault(row.id).then(res => {
          if (res.code === 0) {
            this.$message.success(res.result || '设置成功')
            this.fetchGroupOverview()
            this.refreshLogsIfNeeded(row.id)
          }
        })
      }).catch(() => {})
    },
    handleCreate(row, retry) {
      const api = retry ? tradeGroupRetryCreate : tradeGroupCreateOne
      const message = retry
        ? `确定重试创建组别 ${row.groupCode} 吗？`
        : `确定发起 MT5 组别创建吗？路径：${row.mt5GroupPath}`
      this.$confirm(message, '提示', { type: 'warning' }).then(() => {
        api({ id: row.id, groupCode: row.groupCode }).then(res => {
          if (res.code === 0) {
            this.$message.success(res.result || '操作成功')
            this.fetchGroupOverview()
            this.refreshLogsIfNeeded(row.id)
          }
        })
      }).catch(() => {})
    },
    deleteRow(row) {
      const message = [
        '确定删除当前交易组别吗？',
        `组别ID：${row.groupCode || '-'}`,
        `组别名称：${row.groupName || '-'}`,
        `MT5组路径：${row.mt5GroupPath || '-'}`,
        '删除后仅保留业务日志，当前配置将不再展示。'
      ].join('<br/>')
      this.$confirm(message, '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true
      }).then(() => {
        tradeGroupDeleteBatchIds(String(row.id)).then(res => {
          if (res.code === 0) {
            this.$message.success('删除成功')
            this.fetchGroupOverview()
            this.refreshLogsIfNeeded(row.id)
          }
        })
      }).catch(() => {})
    },
    deleteCountryConfig(country) {
      const rows = this.allGroupData.filter(item => item.countryCode === country.countryCode)
      if (!rows.length) {
        this.rebuildSidebarCountries()
        if (this.currentCountry === country.countryCode) {
          this.currentCountry = 'default'
        }
        return
      }
      const realCount = rows.filter(item => item.groupScene === 1).length
      const demoCount = rows.filter(item => item.groupScene === 2).length
      const guestCount = rows.filter(item => item.groupScene === 3).length
      const message = [
        `确定删除 ${country.countryCode} - ${country.countryName} 的全部交易组别配置吗？`,
        `真实交易组：${realCount} 个`,
        `模拟交易组：${demoCount} 个`,
        `游客组：${guestCount} 个`,
        `合计：${rows.length} 个`,
        '删除后左侧国家配置会同时移除，请确认后继续。'
      ].join('<br/>')
      this.$confirm(message, '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true
      }).then(() => {
        const ids = rows.map(item => item.id).join(',')
        tradeGroupDeleteBatchIds(ids).then(res => {
          if (res.code === 0) {
            this.$message.success('删除成功')
            if (this.currentCountry === country.countryCode) {
              this.currentCountry = 'default'
            }
            this.fetchGroupOverview()
          }
        })
      }).catch(() => {})
    },
    handleCountryMenu(command, country) {
      if (command === 'remove') {
        this.deleteCountryConfig(country)
      }
    },
    openProducts(row) {
      this.currentProductGroup = Object.assign({}, row)
      this.showProductPanel = true
    },
    closeProductPanel() {
      this.showProductPanel = false
      this.currentProductGroup = null
    },
    openLog(row) {
      this.currentLogGroup = {
        id: row.id,
        groupCode: row.groupCode,
        groupName: row.groupName
      }
      this.logDialogTitle = `交易组别业务日志：${row.groupCode || '-'}`
      this.logFilter = this.createEmptyLogFilter()
      this.openLogDialog()
    },
    openAllLogs() {
      this.currentLogGroup = {
        id: null,
        groupCode: '',
        groupName: ''
      }
      this.logDialogTitle = '交易组别业务日志'
      this.logFilter = this.createEmptyLogFilter()
      this.openLogDialog()
    },
    openLogDialog() {
      this.logPageNum = 1
      this.logPageSize = 10
      this.logTableData = []
      this.logTotal = 0
      this.logDialogVisible = true
      this.fetchLogs()
    },
    fetchLogs() {
      this.logLoading = true
      const params = {
        pageNum: this.logPageNum,
        pageSize: this.logPageSize,
        ...this.logFilter
      }
      if (this.currentLogGroup.id) {
        params.groupId = this.currentLogGroup.id
        params.groupCode = this.currentLogGroup.groupCode
      }
      tradeGroupLogList(params).then(res => {
        if (res.code === 0) {
          this.logTableData = (res.result && res.result.list) || []
          this.logTotal = (res.result && res.result.total) || 0
        }
      }).finally(() => {
        this.logLoading = false
      })
    },
    resetLogFilter() {
      this.logFilter = this.createEmptyLogFilter()
      this.logPageNum = 1
      this.fetchLogs()
    },
    refreshLogsIfNeeded(groupId) {
      if (this.logDialogVisible && (!this.currentLogGroup.id || this.currentLogGroup.id === groupId)) {
        this.fetchLogs()
      }
    },
    countryOptionLabel(item) {
      return `${item.countryCode} - ${item.countryCname || item.countryName || item.countryCode}`
    },
    countryText(row) {
      return row.countryName || row.countryCode || '-'
    },
    countryDisplay(countryCode) {
      const item = this.countryOptions.find(option => option.countryCode === countryCode)
      if (item) {
        return `${item.countryCode} - ${item.countryCname || item.countryName || item.countryCode}`
      }
      const sideItem = this.sidebarCountries.find(option => option.countryCode === countryCode)
      if (sideItem) {
        return `${sideItem.countryCode} - ${sideItem.countryName || sideItem.countryCode}`
      }
      return countryCode || '-'
    },
    yesNoText(value) {
      return value === 1 ? '是' : '否'
    },
    // 金额展示统一「千分位 + 2 位小数」（与用户报表页同款），门槛/杠杆列都按数字右对齐
    formatAmount(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) {
        return '-'
      }
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    createStatusText(value) {
      const map = {
        0: '待创建',
        1: '创建中',
        2: '创建成功',
        3: '创建失败'
      }
      return map[value] || '-'
    },
    createStatusTag(value) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'success',
        3: 'danger'
      }
      return map[value] || 'info'
    },
    actionTypeText(value) {
      const map = {
        1: '新增配置',
        2: '编辑配置',
        3: '设为默认',
        4: '取消默认',
        5: '启用组别',
        6: '禁用组别',
        7: '发起创建',
        8: '创建成功',
        9: '创建失败',
        10: '删除配置',
        11: '更新产品数量',
        12: '新增关联产品',
        13: '修改关联产品',
        14: '移除关联产品',
        15: '同步组别杠杆'
      }
      return map[value] || '-'
    },
    actionTypeTag(value) {
      const map = {
        1: 'primary',
        2: 'warning',
        3: 'success',
        4: 'info',
        5: 'success',
        6: 'info',
        7: 'warning',
        8: 'success',
        9: 'danger',
        10: 'danger',
        11: 'primary',
        12: 'success',
        13: 'warning',
        14: 'danger',
        15: 'warning'
      }
      return map[value] || 'info'
    }
  }
}
</script>

<style scoped>
.trade-group-page {
  padding: 16px;
  background: #f5f7fa;
  min-height: 100%;
  overflow-x: hidden;
}

.info-bar {
  margin-bottom: 12px;
  padding: 8px 14px;
  background: #f7fbff;
  border-radius: 8px;
  font-size: 12px;
  color: #7c8aa5;
  border: 1px solid #edf3fb;
}

.trade-group-layout {
  display: flex;
  gap: 16px;
  min-height: 620px;
  min-width: 0;
  flex-wrap: nowrap;
  align-items: stretch;
}

.group-sidebar {
  flex: 0 0 220px;
  width: 220px;
  min-width: 220px;
  max-width: 100%;
  background: #fff;
  border-radius: 4px;
  padding: 12px;
}

.sidebar-add-area {
  margin-bottom: 10px;
}

.select-with-close {
  display: flex;
  align-items: center;
  gap: 4px;
}

.close-select-btn {
  font-size: 16px;
  color: #909399;
  cursor: pointer;
  padding: 4px;
  border-radius: 2px;
  transition: color 0.2s;
}

.close-select-btn:hover {
  color: #f56c6c;
}

.country-list {
  margin-top: 4px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  margin-bottom: 2px;
  transition: background 0.15s;
}

.country-item:hover {
  background: #f0f2f5;
}

.country-item.active {
  background: var(--brand-primary-light);
  color: var(--brand-primary);
  font-weight: 500;
}

.country-icon {
  font-size: 14px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.country-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.country-delete {
  font-size: 14px;
  color: #c0c4cc;
  padding: 0 4px;
  transition: color 0.2s;
}

.country-delete:hover {
  color: #f56c6c;
}

.group-content {
  flex: 1 1 auto;
  width: 0;
  min-width: 0;
  max-width: 100%;
  background: #fff;
  border-radius: 4px;
  padding: 20px 24px 22px;
  box-shadow: none;
  border: 1px solid #e4e7ed;
  overflow: hidden;
}

.content-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.content-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.content-title {
  font-size: 18px;
  line-height: 26px;
  font-weight: 700;
  color: #111827;
  word-break: keep-all;
}

.content-subtitle {
  font-size: 12px;
  color: #98a2b3;
}

.country-type-tag {
  border-color: transparent;
  background: #f0f9eb;
  color: #67c23a;
  border-radius: 999px;
  padding: 0 8px;
}

.header-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.scene-tabs {
  display: flex;
  align-items: center;
  gap: 30px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 18px;
}

.scene-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 0 11px;
  color: #667085;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.scene-tab.active {
  color: var(--brand-primary);
  font-weight: 600;
}

.scene-tab.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -13px;
  height: 2px;
  border-radius: 999px;
  background: var(--brand-primary);
}

.scene-icon {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  flex-shrink: 0;
}

.scene-icon-real {
  background: #52c41a;
}

.scene-icon-demo {
  background: #722ed1;
}

.scene-icon-guest {
  background: #fa8c16;
}

.scene-count {
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  border-radius: 999px;
  background: #f3f4f6;
  color: #606266;
  font-size: 11px;
}

.scene-tab.active .scene-count {
  background: rgba(24, 144, 255, 0.1);
  color: var(--brand-primary);
}

.group-panel {
  min-width: 0;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: none;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.panel-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

/* 横向滚动交给 el-table 自己：列宽超出容器时它会给自己的 body-wrapper 出一条滚条并同步表头。
   这里若再叠一层 overflow-x，就会和外层容器各出一条，表格底部出现两条横杠。 */
.group-table-wrap {
  width: 100%;
  min-width: 0;
}

.log-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.log-group-title {
  min-width: 180px;
  font-weight: 600;
  color: #303133;
}

/* 这里不能写 min-width：一旦表格被撑得比容器宽，el-table 的内部滚条会失效、
   右侧列直接看不见（外层已经没有 overflow 了） */
.group-table {
  border-top: none;
}

::v-deep .group-table .el-table__header-wrapper th {
  background: #f9fafb;
  color: #909399;
  font-weight: 500;
  height: 40px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 12px;
}

::v-deep .group-table .el-table__body-wrapper td {
  color: #344054;
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 13px;
}

::v-deep .group-table .cell {
  line-height: 20px;
}

/* 「设为默认」列只有一个单选圆点：el-radio 默认带 margin-right:30px、label 带 padding-left:10px，
   单元格 align=center 时会整体偏左，这里去掉才能正中。 */
::v-deep .group-table .default-radio.el-radio {
  margin-right: 0;
}

::v-deep .group-table .default-radio .el-radio__label {
  display: none;
}

::v-deep .group-table .el-table__row:hover > td {
  background: #fafefd !important;
}

::v-deep .group-panel .el-button--primary {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
  border-radius: 4px;
}

::v-deep .group-panel .el-button--primary:hover,
::v-deep .group-panel .el-button--primary:focus {
  background: var(--brand-primary-hover);
  border-color: var(--brand-primary-hover);
}

.default-tag {
  border-radius: 999px;
}

.empty-panel {
  padding: 48px 16px;
  text-align: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.empty-desc {
  color: #909399;
  font-size: 13px;
  margin-bottom: 16px;
}

.muted-text {
  color: #c0c4cc;
}

.danger-text {
  color: #f56c6c;
}

.mt5-path-link {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--brand-primary);
  cursor: pointer;
}

.mt5-path-link:hover {
  text-decoration: underline;
}

/* 默认币种：与操作按钮统一用品牌色；浅底 + 品牌边框等价于 el-button--primary.is-plain 的配色 */
.currency-tag {
  background: var(--brand-primary-light);
  border-color: var(--brand-primary-border);
  color: var(--brand-primary);
}

.action-link {
  font-weight: 500;
  font-size: 12px;
  padding: 0;
}

/* 主操作（创建/产品/编辑）统一品牌色，和 el-button--text 默认色一致，
   操作列里不再混第二种蓝 */
.action-link-primary {
  color: var(--brand-primary);
}

.action-link-success {
  color: #0f8f7f;
}

.action-link-warning {
  color: #f59e0b;
}

/* 日志同样用品牌色：操作列只保留语义色（重试橙、删除红）作为区分 */
.action-link-info {
  color: var(--brand-primary);
}

.action-link-danger {
  color: #f56c6c;
}

@media (max-width: 1360px) {
  .content-title-row {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
  }

  .scene-tabs {
    gap: 18px;
  }
}

@media (max-width: 1200px) {
  .group-content {
    padding: 16px;
  }

  .group-sidebar {
    flex-basis: 220px;
    width: 220px;
    min-width: 220px;
  }

  .panel-header {
    padding: 12px 14px;
  }

  .header-actions,
  .panel-actions {
    width: auto;
    margin-left: auto;
    justify-content: flex-end;
  }
}

@media (max-width: 768px) {
  .trade-group-page {
    padding: 12px;
  }

  .trade-group-layout {
    gap: 12px;
  }

  .group-sidebar {
    flex-basis: 208px;
    width: 208px;
    min-width: 208px;
    padding: 10px;
  }

  .group-content {
    padding: 12px;
  }

  .content-header {
    margin-bottom: 14px;
  }

  .content-title {
    font-size: 16px;
    line-height: 24px;
  }

  .scene-tabs {
    gap: 14px;
    margin-bottom: 14px;
  }

  .panel-header {
    padding: 12px;
  }
}
</style>
